import { useEffect, useMemo, useRef, useState } from "react";
import { useDialogFocus } from "../../../../hooks/useDialogFocus";
import { Button } from "../../../../components/Button";
import { Checkbox } from "../../../../components/Checkbox";
import { SelectPopover } from "../../../../components/SelectPopover";
import { TextInput } from "../../../../components/TextInput";
import { getSafeErrorMessage } from "../../../../utils/errorHandler";
import {
  createScholarshipPlayer,
  updateScholarshipPlayer,
} from "../../../../services/scholarships";
import {
  updateProspectProfile,
  type UpdateProspectProfileData,
} from "../../../../services/prospects";
import { JUCO_PLAYER_LEVEL } from "../../../../constants/playerLevels";
import type { JucoYear } from "../../../../utils/roster-class";
import {
  SCHOLARSHIP_CLASSES,
  SCHOLARSHIP_JUCO_CLASSES,
  SCHOLARSHIP_PLAYER_POSITIONS,
  SCHOLARSHIP_UNITS,
  type Campus,
  type Residency,
  type RosterSettings,
  type ScholarshipStoredClass,
  type ScholarshipModel,
  type ScholarshipPlayer,
  type ScholarshipPlayerPosition,
  type ScholarshipPlayerWriteRequest,
} from "../../../../types/scholarships";
import {
  classDisplayLabel,
  effectiveClass,
  graduationYearOptions,
  isGraduationYearClass,
  isJucoClass,
  storedClass,
} from "./scholarshipClasses";
import { positionLabel, type RosterSport } from "./positionGroups";
import { MoneyInput } from "./MoneyInput";
import { PlayerTeamsField } from "./PlayerTeamsField";
import { numberToMoneyString, parseMoneyString } from "./money";
import { formatPlayerPercentFromRaw } from "./scholarshipPercent";
import {
  paletteMarkStyle,
  pitchShapeLabel,
  playsPitcher,
  sportHasArmAngle,
  withCurrentOption,
} from "./rosterPalette";
import "./rosterManagement.css";

interface Props {
  scholarshipId: string;
  scholarshipModel: ScholarshipModel;
  mode: "add" | "edit";
  existingPlayer?: ScholarshipPlayer;
  defaultCost?: number | null;
  onClose: () => void;
  /**
   * After the write lands. `saved` is the row the API returned (null only when there was nothing
   * to write), so a board can show the change at once rather than waiting on its refetch; a
   * control that writes the row back next (the pitching role) must build on this, not the old row.
   */
  onSuccess: (submittedCost: number | null, saved: ScholarshipPlayer | null) => void;
  /** Passed rather than read from context so this stays renderable without an AuthProvider. */
  sport: RosterSport;
  /** The board's option lists: evaluation tags, indicators and the pitching lists. */
  rosterSettings: RosterSettings;
  /**
   * A change that landed on the linked PLAYER RECORD rather than on this row, and so did not go
   * through Save: today that is a team being added or removed. The board refetches, because the
   * row it is showing was built from that record.
   */
  onProspectChanged?: () => void;
  /**
   * A recruiting board's simpler dialog (10/4 feedback): no evaluation tag or DSA, no team box,
   * and the travel team is editable on a linked row, saving to the player record. The hidden
   * fields keep their loaded values and are re-sent, so nothing stored is cleared.
   */
  recruitingBoard?: boolean;
}

/** At most this many indicators on a player, and pitch shapes on a pitcher (the API's limits). */
const MAX_INDICATORS = 3;
const MAX_PITCH_SHAPES = 6;

interface FormState {
  firstName: string;
  lastName: string;
  position: ScholarshipPlayerPosition | "";
  secondaryPosition: ScholarshipPlayerPosition | "";
  playerClass: string;
  bats: string;
  throws: string;
  cost: string;
  award: string;
  scholarshipUnit: number | null;
  nilAmount: string;
  nilPartner: string;
  revShare: string;
  academicAmount: string;
  advisor: string;
  residency: Residency | "";
  campus: Campus | "";
  comments: string;
  isDsa: boolean;
  costTouched: boolean;
  // ----- Roster management -----
  evaluationTraitId: string;
  /** May hold ids the board no longer has; they ride along until the coach changes the set. */
  indicatorIds: string[];
  playerType: string;
  playerStatus: string;
  height: string;
  weight: string;
  school: string;
  travelTeam: string;
  committedSchool: string;
  velo: string;
  armAngle: string;
  pitchShapes: string[];
  pitchingRole: string;
}

const blankForm = (defaultCost?: number | null): FormState => ({
  firstName: "",
  lastName: "",
  position: "",
  secondaryPosition: "",
  playerClass: "",
  bats: "",
  throws: "",
  cost: numberToMoneyString(defaultCost),
  award: "",
  scholarshipUnit: null,
  nilAmount: "",
  nilPartner: "",
  revShare: "",
  academicAmount: "",
  advisor: "",
  residency: "",
  campus: "",
  comments: "",
  isDsa: false,
  costTouched: false,
  evaluationTraitId: "",
  indicatorIds: [],
  playerType: "",
  playerStatus: "",
  height: "",
  weight: "",
  school: "",
  travelTeam: "",
  committedSchool: "",
  velo: "",
  armAngle: "",
  pitchShapes: [],
  pitchingRole: "",
});

/** Whether a roster row is attached to a player record that still exists. */
const isAttached = (p: ScholarshipPlayer): boolean => p.prospectId != null && !p.prospectMissing;

/*
  What the Class field opens on. Normally the row's own value, else the record's graduation year.
  An attached JUCO player with a JC year is shown on the board as "JC 2" (the API derives it from
  the player record), so the field opens on that JC class too, or saving would read as a change.
*/
const seededClass = (p: ScholarshipPlayer): string => {
  const shown = effectiveClass(p);
  if (p.class == null && shown != null && isJucoClass(shown) && isAttached(p)) return shown;
  return storedClass(p) ?? "";
};

const fromPlayer = (p: ScholarshipPlayer): FormState => ({
  firstName: p.firstName,
  lastName: p.lastName,
  position: (p.position ?? "") as ScholarshipPlayerPosition | "",
  secondaryPosition: (p.secondaryPosition ?? "") as ScholarshipPlayerPosition | "",
  // What the board SHOWS, not just the row's own column: an attached player's class comes
  // from their player record, and seeding from p.class alone opened the field blank on a
  // player whose Class column read 2027 — so saving anything else silently cleared it.
  // A JC year is shown as itself on an attached row, so the field opens on what the board shows.
  playerClass: seededClass(p),
  bats: p.bats ?? "",
  throws: p.throws ?? "",
  cost: numberToMoneyString(p.cost),
  award: numberToMoneyString(p.award),
  scholarshipUnit: p.scholarshipUnit ?? null,
  nilAmount: numberToMoneyString(p.nilAmount),
  nilPartner: p.nilPartner ?? "",
  revShare: numberToMoneyString(p.revShare),
  academicAmount: numberToMoneyString(p.academicAmount),
  advisor: p.advisor ?? "",
  residency: (p.residency ?? "") as Residency | "",
  campus: (p.campus ?? "") as Campus | "",
  comments: p.comments ?? "",
  isDsa: p.isDsa,
  costTouched: true,
  evaluationTraitId: p.evaluationTraitId ?? "",
  indicatorIds: p.indicatorIds ?? [],
  playerType: p.playerType ?? "",
  playerStatus: p.playerStatus ?? "",
  height: p.height ?? "",
  weight: p.weight == null ? "" : String(p.weight),
  school: p.school ?? "",
  travelTeam: p.travelTeam ?? "",
  committedSchool: p.committedSchool ?? "",
  velo: p.velo ?? "",
  armAngle: p.armAngle ?? "",
  pitchShapes: p.pitchShapes ?? [],
  pitchingRole: p.pitchingRole ?? "",
});

/** '' → null; otherwise a whole number of pounds, or undefined when it is not one in range. */
const parseWeight = (raw: string): number | null | undefined => {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  return Number.isInteger(n) && n >= 50 && n <= 400 ? n : undefined;
};

/**
 * Valid secondary-position options for a given primary: any position other
 * than the primary itself (pitchers and diamond positions may be mixed).
 */
const secondaryOptionsFor = (
  primary: ScholarshipPlayerPosition | "",
): ScholarshipPlayerPosition[] => {
  if (primary === "") return [];
  return SCHOLARSHIP_PLAYER_POSITIONS.filter((p) => p !== primary);
};

export const ScholarshipPlayerModal = ({
  scholarshipId,
  scholarshipModel,
  mode,
  existingPlayer,
  defaultCost,
  onClose,
  onSuccess,
  sport,
  rosterSettings,
  onProspectChanged,
  recruitingBoard = false,
}: Props) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus({ isOpen: true, dialogRef, handleEscape: false });

  const [form, setForm] = useState<FormState>(() =>
    existingPlayer ? fromPlayer(existingPlayer) : blankForm(defaultCost)
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<{
    firstName?: string;
    lastName?: string;
    weight?: string;
  }>({});

  // True only when the press that began the current click landed on the overlay
  // itself. Without this, drag-selecting text inside a field and releasing over
  // the backdrop fires a click whose target resolves to the overlay, closing the
  // modal mid-selection.
  const pressedOnOverlay = useRef(false);

  useEffect(() => {
    const trigger = (document.activeElement as HTMLElement) ?? null;
    return () => {
      trigger?.focus?.();
    };
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [submitting, onClose]);

  const handleOverlayMouseDown = (e: React.MouseEvent) => {
    pressedOnOverlay.current = e.target === e.currentTarget;
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    const isOverlayClick = e.target === e.currentTarget && pressedOnOverlay.current;
    pressedOnOverlay.current = false;
    if (isOverlayClick && !submitting) onClose();
  };

  /*
    Whether this row draws its class from a player record rather than holding one itself. A class
    year saved here lands on that record, so it changes on every board showing the player and on
    the player's own page. That is deliberate — one player, one graduation year — but a coach
    editing one board should not have to discover it afterwards.

    It also owns height, weight and travel team: the row shows the record's values, those three
    fields are read-only here, and the save sends null for them (see handleSubmit). School is the
    exception -- it writes through to the record, so it stays editable.
  */
  const isLinkedRow = existingPlayer?.prospectId != null && !existingPlayer.prospectMissing;
  /*
    On a recruiting board a linked row's travel team is editable too, and saves to the player
    record (see handleSubmit). A roster board keeps it read-only, as before.
  */
  const travelTeamWritesToRecord = isLinkedRow && recruitingBoard;
  const boardNoun = recruitingBoard ? "board" : "roster";
  const configureLabel = recruitingBoard ? "Configure board" : "Configure roster";
  // What the player record holds for travel team, as far as this dialog knows.
  const recordTravelTeamRef = useRef<string | null>(existingPlayer?.travelTeam?.trim() || null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    const errors: typeof fieldError = {};
    if (!firstName) errors.firstName = "First name is required";
    if (!lastName) errors.lastName = "Last name is required";
    // A linked row's weight is the player record's and is not editable here, so it is neither
    // checked nor sent: a record holding 450 must not stop a coach saving this row's money.
    const weight = isLinkedRow ? null : parseWeight(form.weight);
    if (weight === undefined) errors.weight = "Weight must be a whole number from 50 to 400";

    if (errors.firstName || errors.lastName || errors.weight) {
      setFieldError(errors);
      return;
    }

    setFieldError({});
    setSubmitError(null);
    setSubmitting(true);

    const school = form.school.trim() || null;

    /*
      JC 1-3 is the player record's level and JC year, never a value on the row: the API refuses a
      JC class on a row write, and the board shows one only by deriving it from the record. So on
      an attached row, a JC choice goes to the record as level JUCO plus that JC year, through the
      record's own partial update, which leaves every field it is not sent alone.

      The row's class has to be sent with care alongside it. On an attached row, sending no class
      clears the record's graduation year (the API reads it as the coach clearing what was shown).
      So the row sends the graduation year back unchanged, unless the row holds a class of its own
      (a year of eligibility), which would outrank the JC year and is cleared instead; clearing a
      row's own class leaves the graduation year alone.

      Moving an attached player off a JC class to a class year, or to no class, clears only the
      record's JC year, so what they picked is what the board shows. Their level stays JUCO. A year
      of eligibility needs no clear: it stays on the row and outranks the JC year there, so the
      player keeps their JC year on every other board.
    */
    const initialClass = existingPlayer ? seededClass(existingPlayer) : "";
    const choseJuco = isJucoClass(form.playerClass);
    const leftJuco =
      isJucoClass(initialClass) &&
      (form.playerClass === "" || isGraduationYearClass(form.playerClass));
    const keepRecordClass =
      existingPlayer == null || existingPlayer.class != null
        ? null
        : (storedClass(existingPlayer) as ScholarshipStoredClass | null);
    const rowClass: ScholarshipStoredClass | null =
      isLinkedRow && (choseJuco || (leftJuco && form.playerClass === ""))
        ? keepRecordClass
        : form.playerClass === ""
          ? null
          : (form.playerClass as ScholarshipStoredClass);

    const body: ScholarshipPlayerWriteRequest = {
      firstName,
      lastName,
      position: form.position === "" ? null : form.position,
      secondaryPosition:
        form.secondaryPosition === "" ? null : form.secondaryPosition,
      class: rowClass,
      bats: form.bats.trim() || null,
      throws: form.throws.trim() || null,
      cost: parseMoneyString(form.cost),
      award: parseMoneyString(form.award),
      scholarshipUnit: form.scholarshipUnit,
      nilAmount: parseMoneyString(form.nilAmount),
      nilPartner: form.nilPartner.trim() || null,
      revShare: parseMoneyString(form.revShare),
      // Academic aid rides along with the athletic fields but counts toward none of them —
      // nothing downstream folds it into award, unit or percent.
      academicAmount: parseMoneyString(form.academicAmount),
      advisor: form.advisor.trim() || null,
      residency: form.residency === "" ? null : form.residency,
      campus: form.campus === "" ? null : form.campus,
      comments: form.comments.trim() || null,
      isDsa: form.isDsa,
      // Full replace: every roster field goes back, including the pitching ones while the
      // Pitching group is hidden, so moving a pitcher to 1B and back does not wipe their velo.
      evaluationTraitId: form.evaluationTraitId || null,
      indicatorIds: form.indicatorIds,
      playerType: form.playerType || null,
      playerStatus: form.playerStatus || null,
      // Null on a linked row: these three live on the player record, and the API validates them
      // (weight 50-400, lengths) before it knows the row is linked, so sending the record's
      // values back could refuse the whole save. The API keeps the record's values either way.
      height: isLinkedRow ? null : form.height.trim() || null,
      weight,
      school,
      travelTeam: isLinkedRow ? null : form.travelTeam.trim() || null,
      committedSchool: form.committedSchool.trim() || null,
      velo: form.velo.trim() || null,
      armAngle: form.armAngle || null,
      pitchShapes: form.pitchShapes,
      pitchingRole: form.pitchingRole || null,
    };

    /*
      What this save changes on the linked player record rather than on the row, sent as one partial
      update ahead of the row: the JC class (above), and on a recruiting board the travel team. The
      travel team is a fact about the player, like the class, so it lands on the record and every
      board showing them agrees. It is sent only when the coach changed it, so opening and saving a
      row never rewrites the record with what it already holds. An unlinked row keeps its own travel
      team on the row, as before.
    */
    const prospectId = isLinkedRow ? existingPlayer?.prospectId : null;
    const recordPatch: UpdateProspectProfileData = {};
    const recordAbout: NonNullable<UpdateProspectProfileData["about"]> = {};
    const recordChanges: string[] = [];
    if (prospectId && choseJuco && form.playerClass !== initialClass) {
      recordPatch.playerLevel = JUCO_PLAYER_LEVEL;
      recordPatch.jucoYear = form.playerClass.slice(2) as JucoYear;
      // Both level fields, as the player page sends them, so the save lands whichever side of
      // the level column move the API is on.
      recordAbout.playerLevel = JUCO_PLAYER_LEVEL;
      recordChanges.push("class");
    } else if (prospectId && leftJuco) {
      recordPatch.nullFields = ["jucoYear"];
      recordChanges.push("class");
    }
    const travelTeam = form.travelTeam.trim() || null;
    if (prospectId && travelTeamWritesToRecord && travelTeam !== recordTravelTeamRef.current) {
      // Null clears it: the record's About fields take an explicit null as "remove".
      recordAbout.travelTeam = travelTeam;
      recordChanges.push("travel team");
    }
    if (Object.keys(recordAbout).length > 0) recordPatch.about = recordAbout;

    // Set once the player record has taken its changes, so a failure after it can say so.
    let recordSaved = false;
    try {
      // The record first, so the row the save returns already shows the new class and travel team.
      if (prospectId && recordChanges.length > 0) {
        await updateProspectProfile(prospectId, recordPatch);
        recordSaved = true;
        // The record holds this now, so a second Save after a failed row save compares against
        // it rather than the value the dialog opened with.
        if (recordAbout.travelTeam !== undefined) recordTravelTeamRef.current = travelTeam;
      }
      const saved =
        mode === "add"
          ? await createScholarshipPlayer(scholarshipId, body)
          : existingPlayer
            ? await updateScholarshipPlayer(scholarshipId, existingPlayer.id, body)
            : null;
      onSuccess(body.cost ?? null, saved);
    } catch (err) {
      // Two writes, one after the other: when the record took the class and the row did not, say
      // so rather than "nothing saved". Saving again is safe; both writes are repeatable.
      const savedWhat =
        recordChanges.length > 1
          ? `${recordChanges.join(" and ")} were`
          : `${recordChanges[0] ?? "class"} was`;
      setSubmitError(
        recordSaved
          ? `The ${savedWhat} saved to this player's record, but the rest of the changes did not save. ${getSafeErrorMessage(err, "Save again to keep them.")}`
          : getSafeErrorMessage(err, "Failed to save player"),
      );
      // The record changed even though the row did not: let the board refetch, so cancelling now
      // does not leave it showing the old value.
      if (recordSaved) onProspectChanged?.();
    } finally {
      setSubmitting(false);
    }
  };

  /*
    Both sets, on every board. A recruiting board is chasing a class of 2027 and a scholarship
    roster counts years of eligibility, but neither only ever wants its own: a coach puts a
    current player on a recruiting board, and a scholarship roster carries the Class Summary
    card, which counts graduation years and stays empty forever if the field cannot hold one.
  */
  const classYears = useMemo(() => graduationYearOptions(), []);

  /*
    A stored class that neither list offers — a year outside the rolling window, a legacy
    value — is added back as its own option. Without it the
    select would render blank and quietly save the class away as unset the next time a coach
    edited anything else about the player.
  */
  const offeredClasses: readonly string[] = [
    ...classYears,
    ...SCHOLARSHIP_CLASSES,
    ...(isLinkedRow ? SCHOLARSHIP_JUCO_CLASSES : []),
  ];
  const unofferedClass =
    form.playerClass !== "" && !offeredClasses.includes(form.playerClass)
      ? form.playerClass
      : null;


  // ----- Roster management fields -----
  const knownIndicatorIds = new Set(rosterSettings.indicators.map((i) => i.id));
  const selectedIndicatorCount = form.indicatorIds.filter((id) => knownIndicatorIds.has(id)).length;
  // The first change drops ids the board no longer has, so an invisible id can never hold a slot.
  const toggleIndicator = (id: string) =>
    setForm((prev) => {
      const known = prev.indicatorIds.filter((i) => knownIndicatorIds.has(i));
      if (known.includes(id)) return { ...prev, indicatorIds: known.filter((i) => i !== id) };
      if (known.length >= MAX_INDICATORS) return prev;
      return { ...prev, indicatorIds: [...known, id] };
    });
  const currentTrait = rosterSettings.evaluationTraits.find((t) => t.id === form.evaluationTraitId);
  // A shape the row holds that the board has since dropped stays listed, ticked, so it can be removed.
  const shapeOptions = withCurrentOption(rosterSettings.pitchShapes, form.pitchShapes);
  const toggleShape = (shape: string) =>
    setForm((prev) => {
      if (prev.pitchShapes.includes(shape)) {
        return { ...prev, pitchShapes: prev.pitchShapes.filter((s) => s !== shape) };
      }
      if (prev.pitchShapes.length >= MAX_PITCH_SHAPES) return prev;
      return { ...prev, pitchShapes: [...prev.pitchShapes, shape] };
    });
  const showPitching = playsPitcher({
    position: form.position === "" ? null : form.position,
    secondaryPosition: form.secondaryPosition === "" ? null : form.secondaryPosition,
  });

  const title = mode === "add" ? "Add player" : "Edit player";
  const previewPercent = formatPlayerPercentFromRaw(
    parseMoneyString(form.cost),
    parseMoneyString(form.award)
  );

  return (
    <div ref={dialogRef} tabIndex={-1}
      className="scholarship-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="scholarship-player-modal-title"
      onMouseDown={handleOverlayMouseDown}
      onClick={handleOverlayClick}
    >
      <div className="scholarship-modal scholarship-player-modal">
        <h2
          id="scholarship-player-modal-title"
          className="scholarship-modal__title"
        >
          {title}
        </h2>

        {/*
          This form always types a NEW player onto the board. It used to offer a second tab,
          "From existing prospect", which searched the pool and copied a prospect's name,
          position, bats and throws onto a blank row — and threw the prospect's id away. The row
          looked attached and was not: it did not open the player's record, it did not follow
          their grad year, and a coach who used it reasonably reported the profile "does not link
          up". Attach players is the flow that makes a linked row, so the tab is gone and this
          points at it instead.
        */}
        {mode === "add" && (
          <p className="scholarship-player-modal__typeahead-hint">
            This adds a new player to the board. To pull in someone already in your program, close
            this and use Attach players, which keeps their profile connected.
          </p>
        )}

        {/*
          Autofill off, form and field. Every text field here is about somebody else — a
          recruit's name, their advisor, their NIL partner — so Chrome offering the coach their
          own name and address is never once the right suggestion, and it covers the field the
          coach is typing into. Set on the fields as well as the form because Chrome treats the
          form-level attribute as a default it is free to override on a field it thinks it
          recognises, and "First name" is exactly such a field.
        */}
        <form
          className="scholarship-modal__form scholarship-player-modal__form"
          onSubmit={handleSubmit}
          autoComplete="off"
        >
          <div className="scholarship-player-modal__grid">
            <TextInput
              label="First name *"
              value={form.firstName}
              onChange={(e) => setForm({ ...form, firstName: e.target.value })}
              error={fieldError.firstName}
              disabled={submitting}
              maxLength={100}
              autoFocus
              autoComplete="off"
            />
            <TextInput
              label="Last name *"
              value={form.lastName}
              onChange={(e) => setForm({ ...form, lastName: e.target.value })}
              error={fieldError.lastName}
              disabled={submitting}
              maxLength={100}
              autoComplete="off"
            />
            <div className="text-input">
              <label
                className="text-input__label"
                htmlFor="scholarship-player-position"
              >
                Position
              </label>
              <div className="text-input__wrapper">
                <select
                  id="scholarship-player-position"
                  className="text-input__field scholarship-player-modal__position-select"
                  value={form.position}
                  onChange={(e) => {
                    const newPrimary = e.target.value as
                      | ScholarshipPlayerPosition
                      | "";
                    setForm((prev) => ({
                      ...prev,
                      position: newPrimary,
                      // Drop the secondary if it's no longer valid for the new primary
                      // (different section, or now equal to the primary).
                      secondaryPosition: secondaryOptionsFor(newPrimary).includes(
                        prev.secondaryPosition as ScholarshipPlayerPosition,
                      )
                        ? prev.secondaryPosition
                        : "",
                    }));
                  }}
                  disabled={submitting}
                >
                  <option value="">—</option>
                  {SCHOLARSHIP_PLAYER_POSITIONS.map((p) => (
                    <option key={p} value={p}>
                      {positionLabel(p, sport)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="text-input">
              <label
                className="text-input__label"
                htmlFor="scholarship-player-secondary-position"
              >
                Secondary position
              </label>
              <div className="text-input__wrapper">
                <select
                  id="scholarship-player-secondary-position"
                  className="text-input__field scholarship-player-modal__position-select"
                  value={form.secondaryPosition}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      secondaryPosition: e.target.value as
                        | ScholarshipPlayerPosition
                        | "",
                    })
                  }
                  disabled={submitting || form.position === ""}
                >
                  <option value="">—</option>
                  {secondaryOptionsFor(form.position).map((p) => (
                    <option key={p} value={p}>
                      {positionLabel(p, sport)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="text-input">
              <label
                className="text-input__label"
                htmlFor="scholarship-player-class"
              >
                Class
              </label>
              <div className="text-input__wrapper">
                <select
                  id="scholarship-player-class"
                  className="text-input__field scholarship-player-modal__position-select"
                  value={form.playerClass}
                  onChange={(e) =>
                    setForm({ ...form, playerClass: e.target.value })
                  }
                  disabled={submitting}
                >
                  <option value="">—</option>
                  {unofferedClass && (
                    <option value={unofferedClass}>
                      {classDisplayLabel(unofferedClass)}
                    </option>
                  )}
                  {classYears.length > 0 && (
                    <optgroup label="Class year">
                      {classYears.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  <optgroup label="Year of eligibility">
                    {SCHOLARSHIP_CLASSES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </optgroup>
                  {/* Only for an attached player: a JC year lives on their player record, and a
                      row typed straight onto the board has no record to hold one. */}
                  {isLinkedRow && (
                    <optgroup label="Junior college">
                      {SCHOLARSHIP_JUCO_CLASSES.map((c) => (
                        <option key={c} value={c}>
                          {classDisplayLabel(c)}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
              {isLinkedRow && (
                <p className="scholarship-player-modal__typeahead-hint">
                  A class year or JC year here updates this player's record everywhere.
                </p>
              )}
            </div>
            <div className="text-input">
              <label
                className="text-input__label"
                htmlFor="scholarship-player-bats"
              >
                Bats
              </label>
              <div className="text-input__wrapper">
                <select
                  id="scholarship-player-bats"
                  className="text-input__field scholarship-player-modal__position-select"
                  value={form.bats}
                  onChange={(e) => setForm({ ...form, bats: e.target.value })}
                  disabled={submitting}
                >
                  <option value="">—</option>
                  <option value="R">R</option>
                  <option value="L">L</option>
                  <option value="S">S</option>
                </select>
              </div>
            </div>
            <div className="text-input">
              <label
                className="text-input__label"
                htmlFor="scholarship-player-throws"
              >
                Throws
              </label>
              <div className="text-input__wrapper">
                <select
                  id="scholarship-player-throws"
                  className="text-input__field scholarship-player-modal__position-select"
                  value={form.throws}
                  onChange={(e) => setForm({ ...form, throws: e.target.value })}
                  disabled={submitting}
                >
                  <option value="">—</option>
                  <option value="R">R</option>
                  <option value="L">L</option>
                  <option value="S">S</option>
                </select>
              </div>
            </div>
          </div>

          {/*
            The money comes straight after who the player is. It used to sit below the profile and
            pitching sections, and coaches filling in a roster's scholarships had to scroll past
            all of it on every player (coach feedback, 10/2).
          */}
          <div className="scholarship-player-modal__grid scholarship-player-modal__grid--money">
            <MoneyInput
              label="Cost"
              value={form.cost}
              onChange={(next) =>
                setForm({ ...form, cost: next, costTouched: true })
              }
              disabled={submitting}
            />
            <MoneyInput
              label="Award"
              value={form.award}
              onChange={(next) => setForm({ ...form, award: next })}
              disabled={submitting}
            />
          </div>

          {scholarshipModel === "FullHalf" && (
            <div className="scholarship-player-modal__unit-field">
              <span
                className="scholarship-player-modal__unit-label"
                id="scholarship-player-unit-label"
              >
                Scholarship unit
              </span>
              <div
                className="scholarship-player-modal__unit-chips"
                role="group"
                aria-labelledby="scholarship-player-unit-label"
              >
                {SCHOLARSHIP_UNITS.map((unit) => {
                  const selected = form.scholarshipUnit === unit;
                  return (
                    <button
                      key={unit}
                      type="button"
                      data-testid={`scholarship-unit-chip-${unit}`}
                      aria-pressed={selected}
                      className={
                        selected
                          ? "scholarship-player-modal__unit-chip scholarship-player-modal__unit-chip--active"
                          : "scholarship-player-modal__unit-chip"
                      }
                      onClick={() => setForm({ ...form, scholarshipUnit: unit })}
                      disabled={submitting}
                    >
                      {unit === 1.0 ? "Full (1.0)" : "Half (0.5)"}
                    </button>
                  );
                })}
                <button
                  type="button"
                  data-testid="scholarship-unit-chip-unset"
                  aria-pressed={form.scholarshipUnit === null}
                  className={
                    form.scholarshipUnit === null
                      ? "scholarship-player-modal__unit-chip scholarship-player-modal__unit-chip--active"
                      : "scholarship-player-modal__unit-chip"
                  }
                  onClick={() => setForm({ ...form, scholarshipUnit: null })}
                  disabled={submitting}
                >
                  Unassigned
                </button>
              </div>
            </div>
          )}

          <div
            className={
              previewPercent.isWalkOn
                ? "scholarship-player-modal__percent-preview scholarship-player-modal__percent-preview--walkon"
                : "scholarship-player-modal__percent-preview"
            }
            title={
              previewPercent.isWalkOn
                ? "Walk-on — no scholarship awarded"
                : undefined
            }
            aria-live="polite"
          >
            Scholarship: <strong>{previewPercent.text}</strong>
          </div>

          <fieldset className="scholarship-player-modal__section">
            <legend className="scholarship-player-modal__section-legend">
              Financials & details
            </legend>
            <div className="scholarship-player-modal__grid">
              <MoneyInput
                label="NIL"
                value={form.nilAmount}
                onChange={(next) => setForm({ ...form, nilAmount: next })}
                disabled={submitting}
              />
              <TextInput
                label="NIL partner"
                value={form.nilPartner}
                onChange={(e) =>
                  setForm({ ...form, nilPartner: e.target.value })
                }
                disabled={submitting}
                maxLength={200}
                autoComplete="off"
              />
              <MoneyInput
                label="Rev share"
                value={form.revShare}
                onChange={(next) => setForm({ ...form, revShare: next })}
                disabled={submitting}
              />
              {/* The only place academic money is entered, for both the depth chart and the
                  players tab. Empty stays null ("not recorded") rather than becoming a zero. */}
              <MoneyInput
                label="Academic money"
                value={form.academicAmount}
                onChange={(next) => setForm({ ...form, academicAmount: next })}
                disabled={submitting}
              />
              <TextInput
                label="Advisor"
                value={form.advisor}
                onChange={(e) => setForm({ ...form, advisor: e.target.value })}
                disabled={submitting}
                maxLength={200}
                autoComplete="off"
              />
              <div className="text-input">
                <label
                  className="text-input__label"
                  htmlFor="scholarship-player-residency"
                >
                  Residency
                </label>
                <div className="text-input__wrapper">
                  <select
                    id="scholarship-player-residency"
                    className="text-input__field scholarship-player-modal__position-select"
                    value={form.residency}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        residency: e.target.value as Residency | "",
                      })
                    }
                    disabled={submitting}
                  >
                    <option value="">—</option>
                    <option value="Resident">Resident</option>
                    <option value="NonResident">Non-resident</option>
                  </select>
                </div>
              </div>
              <div className="text-input">
                <label
                  className="text-input__label"
                  htmlFor="scholarship-player-campus"
                >
                  Campus
                </label>
                <div className="text-input__wrapper">
                  <select
                    id="scholarship-player-campus"
                    className="text-input__field scholarship-player-modal__position-select"
                    value={form.campus}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        campus: e.target.value as Campus | "",
                      })
                    }
                    disabled={submitting}
                  >
                    <option value="">—</option>
                    <option value="On">On campus</option>
                    <option value="Off">Off campus</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="text-input scholarship-player-modal__comments-field">
              <label
                className="text-input__label"
                htmlFor="scholarship-player-comments"
              >
                Comments
              </label>
              <div className="text-input__wrapper">
                <textarea
                  id="scholarship-player-comments"
                  className="text-input__field scholarship-player-modal__comments-textarea"
                  value={form.comments}
                  onChange={(e) => setForm({ ...form, comments: e.target.value })}
                  disabled={submitting}
                  maxLength={2000}
                  rows={3}
                />
              </div>
            </div>
          </fieldset>

          <fieldset className="scholarship-player-modal__section">
            <legend className="scholarship-player-modal__section-legend">
              Roster profile
            </legend>
            {/* Not on a recruiting board: DSA and evaluation tag are roster planning tools. Their
                values stay in the form and are re-sent on Save. Player type and status are not
                offered anywhere any more (coach feedback, 10/2), but the form still loads and
                re-sends them, so a value stored before then is kept rather than cleared. */}
            {!recruitingBoard && (
              <div className="scholarship-player-modal__grid">
                <div className="text-input scholarship-player-modal__dsa-field">
                  <Checkbox
                    label="Designated Student Athlete (DSA)"
                    checked={form.isDsa}
                    onChange={(e) => setForm({ ...form, isDsa: e.target.checked })}
                    disabled={submitting}
                    aria-describedby="scholarship-player-dsa-hint"
                  />
                  <p id="scholarship-player-dsa-hint" className="scholarship-player-modal__typeahead-hint">
                    DSA players don't count toward the roster limit. Their
                    scholarship money still counts.
                  </p>
                </div>
                <div className="text-input">
                  <label className="text-input__label" htmlFor="scholarship-player-trait">
                    Evaluation tag
                  </label>
                  <SelectPopover
                    id="scholarship-player-trait"
                    options={rosterSettings.evaluationTraits.map((t) => t.name)}
                    value={currentTrait?.name ?? ""}
                    onChange={(name) => {
                      const trait = rosterSettings.evaluationTraits.find((t) => t.name === name);
                      setForm((prev) => ({ ...prev, evaluationTraitId: trait?.id ?? "" }));
                    }}
                    placeholder="—"
                    disabled={submitting}
                    fullWidth
                  />
                </div>
              </div>
            )}

            <div
              className="scholarship-player-modal__choices"
              role="group"
              aria-labelledby="scholarship-player-indicators-label"
            >
              <span
                id="scholarship-player-indicators-label"
                className="scholarship-player-modal__field-label"
              >
                Indicators <span className="scholarship-player-modal__limit">(up to {MAX_INDICATORS})</span>
              </span>
              {rosterSettings.indicators.length === 0 ? (
                <p className="scholarship-player-modal__typeahead-hint">
                  No indicators set up for this {boardNoun}. Add them in {configureLabel}.
                </p>
              ) : (
                <div className="scholarship-player-modal__choice-list">
                  {rosterSettings.indicators.map((indicator) => {
                    const checked = form.indicatorIds.includes(indicator.id);
                    return (
                      <Checkbox
                        key={indicator.id}
                        checked={checked}
                        disabled={
                          submitting || (!checked && selectedIndicatorCount >= MAX_INDICATORS)
                        }
                        onChange={() => toggleIndicator(indicator.id)}
                        label={
                          <span className="scholarship-player-modal__indicator-label">
                            <span
                              className="roster-indicator-dot"
                              style={paletteMarkStyle(indicator.color)}
                              aria-hidden="true"
                            />
                            {indicator.name}
                          </span>
                        }
                      />
                    );
                  })}
                </div>
              )}
            </div>

            <div className="scholarship-player-modal__grid">
              <TextInput
                label="Height"
                value={form.height}
                onChange={(e) => setForm({ ...form, height: e.target.value })}
                disabled={submitting || isLinkedRow}
                maxLength={10}
                placeholder={`e.g. 6'1"`}
                autoComplete="off"
              />
              <TextInput
                label="Weight (lb)"
                inputMode="numeric"
                value={form.weight}
                onChange={(e) => setForm({ ...form, weight: e.target.value })}
                error={fieldError.weight}
                disabled={submitting || isLinkedRow}
                maxLength={3}
                autoComplete="off"
              />
              {/*
                Writable on a linked row too. It used to be greyed out with "edit them on the
                player's page", so a coach on the board could see the field and not use it. School
                is a fact about the player, so the save lands on the player record and every board
                showing them agrees about it -- the same rule the class already follows.
              */}
              <TextInput
                label="School"
                value={form.school}
                onChange={(e) => setForm({ ...form, school: e.target.value })}
                disabled={submitting}
                maxLength={100}
                autoComplete="off"
              />
              <TextInput
                label="Travel team"
                value={form.travelTeam}
                onChange={(e) => setForm({ ...form, travelTeam: e.target.value })}
                disabled={submitting || (isLinkedRow && !travelTeamWritesToRecord)}
                maxLength={100}
                autoComplete="off"
              />
              <TextInput
                label="Committed to"
                value={form.committedSchool}
                onChange={(e) => setForm({ ...form, committedSchool: e.target.value })}
                disabled={submitting}
                maxLength={100}
                placeholder="School they committed to"
                autoComplete="off"
              />
            </div>
            {isLinkedRow && (
              <p className="scholarship-player-modal__typeahead-hint">
                {travelTeamWritesToRecord
                  ? "Height and weight come from this player's record. Change them on the player's page. A travel team saved here updates their record, so it changes everywhere."
                  : "Height, weight and travel team come from this player's record. Change them on the player's page. A school saved here updates their record, so it changes everywhere."}
              </p>
            )}

            {/* Not on a recruiting board: putting a recruit on one of your teams is roster work. */}
            {isLinkedRow && existingPlayer?.prospectId && !recruitingBoard && (
              <PlayerTeamsField
                prospectId={existingPlayer.prospectId}
                playerName={`${form.firstName} ${form.lastName}`.trim() || 'this player'}
                teams={existingPlayer.teams ?? []}
                disabled={submitting}
                onChanged={() => onProspectChanged?.()}
              />
            )}
          </fieldset>

          {showPitching && (
            <fieldset className="scholarship-player-modal__section">
              <legend className="scholarship-player-modal__section-legend">Pitching</legend>
              <div className="scholarship-player-modal__grid">
                <TextInput
                  label="Velo"
                  value={form.velo}
                  onChange={(e) => setForm({ ...form, velo: e.target.value })}
                  disabled={submitting}
                  maxLength={20}
                  placeholder="e.g. 88-91"
                  autoComplete="off"
                />
                {/* Softball hides the picker; the form still holds the stored value, so the
                    save sends it back untouched rather than clearing it. */}
                {sportHasArmAngle(sport) && (
                  <div className="text-input">
                    <label className="text-input__label" htmlFor="scholarship-player-arm-angle">
                      Arm angle
                    </label>
                    <SelectPopover
                      id="scholarship-player-arm-angle"
                      options={withCurrentOption(rosterSettings.armAngles, form.armAngle)}
                      value={form.armAngle}
                      onChange={(next) => setForm((prev) => ({ ...prev, armAngle: next }))}
                      placeholder="—"
                      disabled={submitting}
                      fullWidth
                    />
                  </div>
                )}
                <div className="text-input">
                  <label className="text-input__label" htmlFor="scholarship-player-role">
                    Role
                  </label>
                  <SelectPopover
                    id="scholarship-player-role"
                    options={withCurrentOption(rosterSettings.pitchingRoles, form.pitchingRole)}
                    value={form.pitchingRole}
                    onChange={(next) => setForm((prev) => ({ ...prev, pitchingRole: next }))}
                    placeholder="—"
                    disabled={submitting}
                    fullWidth
                  />
                </div>
              </div>
              <div
                className="scholarship-player-modal__choices"
                role="group"
                aria-labelledby="scholarship-player-shapes-label"
              >
                <span
                  id="scholarship-player-shapes-label"
                  className="scholarship-player-modal__field-label"
                >
                  Pitch shapes{" "}
                  <span className="scholarship-player-modal__limit">(up to {MAX_PITCH_SHAPES})</span>
                </span>
                {shapeOptions.length === 0 ? (
                  <p className="scholarship-player-modal__typeahead-hint">
                    No pitch shapes set up for this {boardNoun}. Add them in {configureLabel}.
                  </p>
                ) : (
                  <div className="scholarship-player-modal__choice-list">
                    {shapeOptions.map((shape) => {
                      const checked = form.pitchShapes.includes(shape);
                      return (
                        <Checkbox
                          key={shape}
                          checked={checked}
                          disabled={
                            submitting ||
                            (!checked && form.pitchShapes.length >= MAX_PITCH_SHAPES)
                          }
                          onChange={() => toggleShape(shape)}
                          label={pitchShapeLabel(shape)}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            </fieldset>
          )}

          {submitError && (
            <div className="scholarship-modal__error" role="alert">
              {submitError}
            </div>
          )}

          <div className="scholarship-modal__actions">
            <Button
              variant="secondary"
              type="button"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? "Saving..." : "Save"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
